import { useTranslation } from 'react-i18next'
import { extractVariableName } from './variables-ops'

interface ValueFieldProps {
  content: string
  suggestedName: string
  onChange: (content: string) => void
  placeholder?: string
}

/**
 * A value cell that is either a Fixed literal or a {variable}. The variable
 * form writes `{name}` into the content so the existing variable-collection
 * path (collectAllVariableNames/mergeVariables) exposes it on the dropped block.
 */
export function ValueField({ content, suggestedName, onChange, placeholder }: ValueFieldProps) {
  const { t } = useTranslation()
  const varName = extractVariableName(content)
  const isVariable = varName !== undefined

  return (
    <div className="space-y-1">
      <div className="inline-flex rounded border border-stone-300 text-[11px] dark:border-stone-700">
        <button
          type="button"
          data-testid="vf-mode-fixed"
          onClick={() => { if (isVariable) onChange('') }}
          className={`px-2 py-0.5 ${!isVariable ? 'bg-stone-200 dark:bg-stone-700' : ''}`}
        >{t('valueField.fixed')}</button>
        <button
          type="button"
          data-testid="vf-mode-variable"
          onClick={() => { if (!isVariable) onChange(`{${suggestedName || 'value'}}`) }}
          className={`px-2 py-0.5 ${isVariable ? 'bg-fuchsia-200 dark:bg-fuchsia-800' : ''}`}
        >{t('valueField.variable')}</button>
      </div>
      {isVariable ? (
        <div className="flex items-center gap-1 font-mono text-sm">
          <span className="text-fuchsia-600">{'{'}</span>
          <input
            data-testid="vf-variable-name"
            value={varName}
            onChange={e => onChange(`{${e.target.value.replace(/[^a-zA-Z0-9_]/g, '')}}`)}
            className="w-full rounded border border-stone-300 px-1 py-0.5 dark:border-stone-700 dark:bg-stone-950"
          />
          <span className="text-fuchsia-600">{'}'}</span>
        </div>
      ) : (
        <input
          data-testid="vf-fixed-input"
          value={content}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder ?? t('valueField.placeholder')}
          className="w-full rounded border border-stone-300 px-2 py-1 font-mono text-sm dark:border-stone-700 dark:bg-stone-950"
        />
      )}
    </div>
  )
}
